import { createModel } from '@rematch/core'
import * as firebase from 'firebase'
import * as R from 'ramda'
import { dispatch, select, store } from '../store'
import { toggleElement } from '../utils'

interface IInsightState {
  [lectureId: string]: IInsight
}

const errorInsight: IInsight = {
  ts: firebase.firestore.Timestamp.now(),
  text: '觀點讀取失敗',
  materials: [],
  speakerId: '',
  revisions: []
}

async function fetchInsight(insightId: string) {
  const ref = await firebase
    .firestore()
    .collection('insights')
    .doc(insightId)
    .get()
  const insight = ref.data()
  if (insight) {
    const speakerId = (insight as IInsight).speakerId
    const speaker = select.users.getById(store.getState(), speakerId)
    if (!speaker) {
      dispatch.users.fetchUsers([speakerId])
    }
    return insight
  } else {
    throw Error('Cannot fetch insight')
  }
}

export default createModel({
  state: {} as IInsightState,
  reducers: {
    updateById: (
      state: IInsightState,
      payload: { insightId: string; insight: IInsight }
    ): IInsightState => ({
      ...state,
      [payload.insightId]: payload.insight
    })
  },
  effects: {
    async fetchInsights(insightIds: string[]) {
      insightIds.map(insightId => {
        fetchInsight(insightId)
          .then(insight => this.updateById({ insightId, insight }))
          .catch(error =>
            this.updateById({ insightId, insight: errorInsight })
          )
      })
    },
    async addInsight({
      speakerId,
      lectureId,
      unitIdx,
      chapterIdx
    }: {
      speakerId: string;
      lectureId: string;
      unitIdx: number;
      chapterIdx: number;
    }) {
      const insight: IInsight = {
        ts: firebase.firestore.Timestamp.now(),
        text: '未命名區塊',
        speakerId,
        materials: [],
        revisions: []
      }
      // check existence
      const lecture = select.lectures.getById(store.getState(), lectureId)
      if (lecture === null) {
        throw Error('lecture not found')
      }
      const unit = lecture.units[unitIdx]
      const chapter = unit.chapters[chapterIdx]
      if (chapter === null) {
        throw Error('chapter not found')
      }
      // add insight into database
      const insightRef = await firebase
        .firestore()
        .collection('insights')
        .add(insight)
      const insightSnap = await insightRef.get()
      // add insight into chapter
      const insightId = insightSnap.id
      dispatch.lectures.updateLecture({
        lectureId,
        updated: {
          units: [
            ...lecture.units.slice(0, unitIdx),
            {
              ...unit,
              chapters: [
                ...unit.chapters.slice(0, chapterIdx),
                {
                  ...chapter,
                  insightIds: [...chapter.insightIds, insightId]
                },
                ...unit.chapters.slice(chapterIdx + 1)
              ]
            },
            ...lecture.units.slice(unitIdx + 1)
          ]
        }
      })
      this.updateById({ insightId, insight })
    },
    async deleteInsight({
      lectureId,
      insightId,
      unitIdx,
      chapterIdx
    }: {
      lectureId: string;
      unitIdx: number;
      chapterIdx: number;
      insightId: string;
    }) {
      // check existence
      const lecture = select.lectures.getById(store.getState(), lectureId)
      if (lecture === null) {
        throw Error('lecture not found')
      }
      const unit = lecture.units[unitIdx]
      const chapter = unit.chapters[chapterIdx]
      if (chapter === null) {
        throw Error('chapter not found')
      }
      // delete insight
      await firebase
        .firestore()
        .collection('insights')
        .doc(insightId)
        .delete()
      this.updateById({ insightId, insight: errorInsight })
      // delete insight from chapter
      const updatedInsightIds = R.filter(
        _ => _ !== insightId,
        chapter.insightIds
      )
      dispatch.lectures.updateLecture({
        lectureId,
        updated: {
          units: [
            ...lecture.units.slice(0, unitIdx),
            {
              ...unit,
              chapters: [
                ...unit.chapters.slice(0, chapterIdx),
                {
                  ...chapter,
                  insightIds: updatedInsightIds
                },
                ...unit.chapters.slice(chapterIdx + 1)
              ]
            },
            ...lecture.units.slice(unitIdx + 1)
          ]
        }
      })
    },
    async updateInsight(payload: {
      insightId: string;
      updated: Partial<IInsight>;
    }) {
      const { insightId, updated } = payload
      const insight = select.insights.getById(store.getState(), insightId)
      const updatedInsight = {
        ...insight,
        ...updated
      }
      firebase
        .firestore()
        .collection('insights')
        .doc(insightId)
        .set(updatedInsight)
      this.updateById({ insightId, insight: updatedInsight })
    },
    async addMaterial(payload: { insightId: string; material: IMaterial }) {
      const { insightId, material } = payload
      const insight = select.insights.getById(store.getState(), insightId)
      if (insight === null) {
        throw Error('no current insight')
      }
      const updatedMaterials = [...insight.materials, material]
      const updatedInsight = { ...insight, materials: updatedMaterials }
      firebase
        .firestore()
        .collection('insights')
        .doc(insightId)
        .set(updatedInsight)
      this.updateById({ insightId, insight: updatedInsight })
    },
    async addReply(payload: {
      chapterIdx: number;
      insightId: string;
      materialIdx: number;
      reply: IReply;
      lectureId: string;
    }) {
      const { insightId, chapterIdx, materialIdx, reply, lectureId } = payload
      const rootState = store.getState()
      const lecture = select.lectures.getById(rootState, lectureId)
      const insight = select.insights.getById(rootState, insightId)
      if (lecture === null || insight === null) {
        throw Error('no current lecture or insight')
      }
      if (!reply.text.trim()) {
        throw Error('請輸入留言')
      }
      // add reply into firestore
      const replyRef = await firebase
        .firestore()
        .collection('replies')
        .add(reply)
      const replySnap = await replyRef.get()
      const replyId = replySnap.id
      const updatedMaterial: IMaterial = {
        ...insight.materials[materialIdx],
        replyIds: [...insight.materials[materialIdx].replyIds, replyId]
      }
      const updatedMaterials: IMaterial[] = [
        ...insight.materials.slice(0, materialIdx),
        updatedMaterial,
        ...insight.materials.slice(materialIdx + 1)
      ]
      const updatedInsight: IInsight = {
        ...insight,
        materials: updatedMaterials
      }
      dispatch.replies.fetchReplies([replyId])
      firebase
        .firestore()
        .collection('insights')
        .doc(insightId)
        .set(updatedInsight)
      const event: IReplyEvent = {
        type: 'NEW_REPLY',
        payload: {
          isSpeaker: R.contains(reply.replierId, lecture.speakerIds),
          lectureId,
          chapterIdx,
          insightId,
          materialIdx,
          replyId,
          reply
        }
      }
      firebase
        .firestore()
        .collection('events')
        .add(event)
      this.updateById({ insightId, insight: updatedInsight })
    },
    async toggleReaction(payload: {
      insightId: string;
      chapterIdx: number;
      materialIdx: number;
      reaction: string;
      lectureId: string;
    }) {
      const currentUser = firebase.auth().currentUser
      if (currentUser === null) {
        throw Error('請先登入會員')
      }
      const {
        lectureId,
        chapterIdx,
        insightId,
        materialIdx,
        reaction
      } = payload
      const insight = select.insights.getById(store.getState(), insightId)
      if (insight === null) {
        throw Error('no current insight')
      }
      const material: IMaterial = insight.materials[materialIdx]
      const updatedReactions = material.reactions[reaction]
        ? {
            ...material.reactions,
            [reaction]: toggleElement(
              currentUser.uid,
              material.reactions[reaction]
            )
          }
        : {
            ...material.reactions,
            [reaction]: [currentUser.uid]
          }
      const updatedMaterial: IMaterial = {
        ...insight.materials[materialIdx],
        reactions: updatedReactions
      }
      const updatedMaterials: IMaterial[] = [
        ...insight.materials.slice(0, materialIdx),
        updatedMaterial,
        ...insight.materials.slice(materialIdx + 1)
      ]
      const updatedInsight: IInsight = {
        ...insight,
        materials: updatedMaterials
      }
      // update database
      firebase
        .firestore()
        .collection('insights')
        .doc(insightId)
        .set(updatedInsight)
      // active reaction
      if (R.contains(currentUser.uid, updatedMaterial.reactions[reaction])) {
        const event: IMaterialReactionEvent = {
          type: 'NEW_MATERIAL_REACTION',
          payload: {
            lectureId,
            chapterIdx,
            insightId,
            materialIdx,
            userId: currentUser.uid
          }
        }
        firebase
          .firestore()
          .collection('events')
          .add(event)
      }
      this.updateById({ insightId, insight: updatedInsight })
    }
  },
  selectors: {
    getById: (state: IInsightState, insightId: string) => {
      return state[insightId] ? state[insightId] : null
    }
  }
})



// WEBPACK FOOTER //
// ./src/models/insights.ts